import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import Layout from './components/Layout';
import EnhancedAdminDashboard from './components/EnhancedAdminDashboard';
import ManagerDashboard from './components/ManagerDashboard';
import UserDashboard from './pages/UserDashboard';
import Login from './components/Login';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SessionManager } from './components/SessionManager';
import { onSessionExpired } from './services/api';

// Validate required environment variables
const requiredEnvVars = {
  REACT_APP_USER_POOL_ID: process.env.REACT_APP_USER_POOL_ID,
  REACT_APP_USER_POOL_CLIENT_ID: process.env.REACT_APP_USER_POOL_CLIENT_ID,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  REACT_APP_AWS_REGION: process.env.REACT_APP_AWS_REGION
};

for (const [key, value] of Object.entries(requiredEnvVars)) {
  if (!value) {
    console.error(`Missing required environment variable: ${key}`);
    throw new Error(`Configuration error: ${key} is not defined`);
  }
}

// Configure Amplify v6
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: requiredEnvVars.REACT_APP_USER_POOL_ID!,
      userPoolClientId: requiredEnvVars.REACT_APP_USER_POOL_CLIENT_ID!
    }
  },
  API: {
    REST: {
      'api': {
        endpoint: requiredEnvVars.REACT_APP_API_URL!,
        region: requiredEnvVars.REACT_APP_AWS_REGION!
      }
    }
  }
});

interface User {
  username?: string;
  signInDetails?: {
    loginId?: string;
  };
  signInUserSession?: {
    accessToken?: {
      payload?: {
        'custom:role'?: string;
        'cognito:groups'?: string[];
      };
    };
    idToken?: {
      payload?: {
        'custom:role'?: string;
      };
    };
  };
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);

  // Handle session expiry (from idle timeout or token expiry)
  const handleSessionExpired = useCallback(async (message?: string) => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error during sign out:', error);
    }
    setUser(null);
    setSessionExpiredMessage(message || 'Your session has expired due to inactivity. Please log in again.');
  }, []);

  // Register session expiry callback for API calls
  useEffect(() => {
    onSessionExpired(() => {
      handleSessionExpired('Your session has expired. Please log in again.');
    });
  }, [handleSessionExpired]);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.log('No authenticated user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (authenticatedUser: User) => {
    setSessionExpiredMessage(null); // Clear any previous session expired message
    setUser(authenticatedUser);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Login onLogin={handleLogin} sessionExpiredMessage={sessionExpiredMessage} />
        <Toaster position="top-right" />
      </>
    );
  }

  return (
    <>
      <NotificationProvider>
        <AuthProvider user={user} signOut={handleSignOut}>
          <SessionManager 
            onSessionExpired={() => handleSessionExpired()}
            idleTimeout={15} // 15 minutes idle timeout
          >
            <Router>
              <Layout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardRouter user={user} />} />
                  <Route path="/admin" element={<EnhancedAdminDashboard />} />
                  <Route path="/admin/enhanced" element={<EnhancedAdminDashboard />} />
                  <Route path="/manager" element={<ManagerDashboard />} />
                </Routes>
              </Layout>
            </Router>
          </SessionManager>
        </AuthProvider>
      </NotificationProvider>
      <Toaster position="top-right" />
    </>
  );
}

function DashboardRouter({ user }: { user: User }) {
  const [userRole, setUserRole] = useState<'user' | 'manager' | 'admin'>('user');
  const [loading, setLoading] = useState(true);
  const [dashboardMode, setDashboardMode] = useState<'management' | 'browser'>('management');

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        // Get the current auth session with tokens
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken;
        
        // Check for groups in the access token
        const groupsClaim = accessToken?.payload?.['cognito:groups'];
        const groups = Array.isArray(groupsClaim) ? groupsClaim : [];
        
        let role: 'user' | 'manager' | 'admin' = 'user';
        
        if (groups.includes('admin')) {
          role = 'admin';
        } else if (groups.includes('manager')) {
          role = 'manager';
        }
        
        setUserRole(role);
      } catch (error) {
        console.error('Error checking user role:', error);
        setUserRole('user');
      } finally {
        setLoading(false);
      }
    };

    checkUserRole();
  }, [user]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Dashboard switch handlers
  const switchToBrowser = () => setDashboardMode('browser');
  const switchToManagement = () => setDashboardMode('management');

  // Route based on user role and dashboard mode
  switch (userRole) {
    case 'admin':
      return dashboardMode === 'management' ? 
        <EnhancedAdminDashboard 
          onSwitchToBrowser={switchToBrowser}
          dashboardMode={dashboardMode}
        /> :
        <UserDashboard 
          onSwitchToManagement={switchToManagement}
          dashboardMode={dashboardMode}
          userRole={userRole}
        />;
    case 'manager':
      return dashboardMode === 'management' ? 
        <ManagerDashboard 
          onSwitchToBrowser={switchToBrowser}
          dashboardMode={dashboardMode}
        /> :
        <UserDashboard 
          onSwitchToManagement={switchToManagement}
          dashboardMode={dashboardMode}
          userRole={userRole}
        />;
    default:
      return <UserDashboard userRole={userRole} />;
  }
}

export default App;